import { useEffect, useState } from 'react';
import type { SharedYear } from './lib/types';

/**
 * The evaluator's page (#612): a family's read-only share of one child's school year, opened from a link with no
 * account. Formal and light, like the portfolio PDF, whatever theme the family uses.
 */

const fmt = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const num = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 });
const ord = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th')}`;
const gradeLabel = (g: string) => (!g.trim() ? '' : /grade|kindergarten|pre-?k/i.test(g) ? g : `${g} grade`);
const STATUS: Record<string, string> = { met: 'Requirement met', 'on-track': 'On track', behind: 'Behind pace', 'not-started': 'Not started', 'no-target': 'No day or hour count' };

export function SharePage({ token }: { token: string }) {
  const [data, setData] = useState<SharedYear | null>(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    document.title = 'Shared school year · ClassGuardian';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    fetch(`/api/share/${encodeURIComponent(token)}`)
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (!r.ok) throw new Error(body?.error ?? 'This link doesn’t work.');
        setData(body as SharedYear);
      })
      .catch((e) => setError((e as Error).message));
    return () => meta.remove();
  }, [token]);

  if (error)
    return (
      <div className="share-page">
        <div className="share-gone">
          <h1>This link doesn’t work any more</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  if (!data) return <div className="share-page" aria-busy="true" />;
  const base = `/api/share/${encodeURIComponent(token)}`;
  const max = Math.max(1, ...data.subjects.map((s) => s.hours));
  const until = new Date(data.expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const year = data.from.slice(0, 4) === data.to.slice(0, 4) ? data.from.slice(0, 4) : `${data.from.slice(0, 4)}–${data.to.slice(2, 4)}`;

  return (
    <div className="share-page">
      <header className="share-band">
        <div className="share-head">
          <div className="share-title">
            <span className="share-kicker">Homeschool portfolio · {year} school year</span>
            <h1>{data.child.name}</h1>
            <span className="share-sub">
              {[gradeLabel(data.child.grade), data.requirement && `${data.requirement.state} · ${data.requirement.option}`, `shared by ${data.sharedBy} with ${data.label}, until ${until}`]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
          {data.pdf && (
            <a className="share-pdf" href={`${base}/portfolio.pdf`}>
              Download the portfolio PDF
            </a>
          )}
        </div>
      </header>
      <main className="share-main">
        <div className="share-stats">
          <div>
            <strong>
              {data.stats.days}
              {data.requirement?.days ? <small> of {data.requirement.days}</small> : null}
            </strong>
            <span>Days attended</span>
          </div>
          <div>
            <strong>{num(data.stats.hours)}</strong>
            <span>Hours recorded</span>
          </div>
          {data.sections.includes('reading') && (
            <div>
              <strong>{data.stats.books}</strong>
              <span>Books read</span>
            </div>
          )}
          {data.sections.includes('samples') && (
            <div>
              <strong>{data.stats.samples}</strong>
              <span>Work samples</span>
            </div>
          )}
          {data.requirement && (
            <div>
              <strong className="share-status">{STATUS[data.requirement.status ?? ''] ?? data.requirement.state}</strong>
              <span>
                {[data.requirement.days && `${data.requirement.days} days`, data.requirement.hours && `${num(data.requirement.hours)} hours`].filter(Boolean).join(' or ') || data.requirement.option}
              </span>
            </div>
          )}
        </div>

        {data.requiredSubjects?.length > 0 && (
          <section className="share-card">
            <h2>Required subjects</h2>
            <table className="share-table">
              <thead>
                <tr>
                  <th>Required</th>
                  <th>Taught as</th>
                  <th>Hours</th>
                  <th>Lessons</th>
                </tr>
              </thead>
              <tbody>
                {data.requiredSubjects.map((r) => (
                  <tr key={r.name}>
                    <td>{r.name}</td>
                    <td>{r.subjects.join(', ') || '—'}</td>
                    <td>{r.hours ? num(r.hours) : '—'}</td>
                    <td>{r.lessons || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <div className="share-grid">
          {data.subjects.length > 0 && (
            <section className="share-card">
              <h2>Time by subject</h2>
              <div className="share-bars">
                {data.subjects.map((s) => (
                  <div key={s.subject} className="share-bar">
                    <span>{s.subject}</span>
                    <div className="share-track" role="img" aria-label={`${s.subject}: ${num(s.hours)} hours`}>
                      <div style={{ width: `${(s.hours / max) * 100}%` }} />
                    </div>
                    <span className="share-num">{num(s.hours)} h</span>
                  </div>
                ))}
              </div>
            </section>
          )}
          {data.grades.length > 0 && (
            <section className="share-card">
              <h2>Grades</h2>
              <table className="share-table">
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Average</th>
                    <th>Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {data.grades.map((g) => (
                    <tr key={g.subject}>
                      <td>{g.subject}</td>
                      <td>{g.pct}%</td>
                      <td>{g.letter}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>

        {data.tests.length > 0 && (
          <section className="share-card">
            <h2>Tests and evaluations</h2>
            {data.tests.map((t) => (
              <div key={t.id} className="share-test">
                <div>
                  <strong>{t.name}</strong> · {fmt(t.date)}
                  {t.grade && ` · ${gradeLabel(t.grade)}`}
                  {t.givenBy && ` · ${t.givenBy}`}
                  {!!t.scores.length && <div className="share-muted">{t.scores.map((s) => [s.subject, s.score, s.percentile !== null && `${ord(s.percentile)} percentile`].filter(Boolean).join(' ')).join(' · ')}</div>}
                  {t.note && <div className="share-muted">“{t.note}”</div>}
                </div>
                {t.file && <a href={`${base}/tests/${t.id}/file`}>Report ({t.file.mime === 'application/pdf' ? 'PDF' : 'photo'})</a>}
              </div>
            ))}
          </section>
        )}

        {data.trips.length > 0 && (
          <section className="share-card">
            <h2>Field trips</h2>
            {data.trips.map((t, i) => (
              <div key={i} className="share-test">
                <div>
                  <strong>{t.place}</strong> · {fmt(t.date)}
                  {t.hours > 0 && ` · ${t.hours} hours${t.subject ? ` of ${t.subject}` : ''}`}
                  {t.went.length > 1 && <div className="share-muted">With {t.went.join(' and ')}</div>}
                  {t.learned && <div className="share-muted">{t.learned}</div>}
                </div>
              </div>
            ))}
          </section>
        )}

        {data.samples.length > 0 && (
          <section className="share-card">
            <div className="share-card-head">
              <h2>Work samples</h2>
              <span className="share-muted">{data.samples.length} · open one to see it full size</span>
            </div>
            <div className="share-samples">
              {data.samples.map((s) => (
                <a
                  key={s.id}
                  className="share-sample"
                  href={`${base}/samples/${s.id}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => {
                    if (s.mime.startsWith('image/')) {
                      e.preventDefault();
                      setOpen(s.id);
                    }
                  }}
                >
                  {s.mime.startsWith('image/') ? <img src={`${base}/samples/${s.id}`} alt={s.caption || s.name} loading="lazy" /> : <span className="share-pdf-thumb">PDF</span>}
                  <strong>{s.subject?.title || s.caption || s.name}</strong>
                  <span className="share-muted">{[s.subject?.subject, fmt(s.date)].filter(Boolean).join(' · ')}</span>
                </a>
              ))}
            </div>
          </section>
        )}

        {data.reading.length > 0 && (
          <section className="share-card">
            <h2>Reading list</h2>
            <ul className="share-list">
              {data.reading.map((r, i) => (
                <li key={i}>
                  {r.title} <span className="share-muted">· {fmt(r.date)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {data.journal.length > 0 && (
          <section className="share-card">
            <h2>Homeschool journal</h2>
            {data.journal.map((j) => (
              <div key={j.date} className="share-journal">
                <span className="share-kicker dark">{fmt(j.date)}</span>
                <div>{j.html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()}</div>
              </div>
            ))}
          </section>
        )}
        <p className="share-muted small">Read-only, shared from ClassGuardian by the family.</p>
      </main>
      {open && (
        <div className="share-lightbox" role="dialog" aria-label="Work sample" onClick={() => setOpen(null)}>
          <img src={`${base}/samples/${open}`} alt="" />
          <button className="share-close" onClick={() => setOpen(null)}>
            Close
          </button>
        </div>
      )}
    </div>
  );
}
